import { useState, useEffect } from "react"
import { Head, router } from "@inertiajs/react"
import { InlineWidget, useCalendlyEventListener } from "react-calendly"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { CheckCircle, Calendar, ArrowLeft } from "lucide-react"
import { PageLayout } from "@/components/VocationalTest/PageLayout"
import { NavigationHeader } from "@/components/VocationalTest/NavigationHeader"

interface Props {
  user: {
    name: string | null
    email: string
  }
  alreadyScheduled: boolean
  scheduledAt: string | null
  inviteeUri: string | null
  calendlyUrl: string
}

export default function Mentorship({ user, alreadyScheduled, scheduledAt, inviteeUri, calendlyUrl }: Props) {
  const [hasScheduled, setHasScheduled] = useState(alreadyScheduled)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Listener para quando o usuário agendar
  useCalendlyEventListener({
    onEventScheduled: (e) => {
      const payload = e.data.payload
      const inviteeUri = payload.invitee?.uri || ""

      setIsSubmitting(true)

      // Salva no backend
      router.post("/mentorship/scheduled", {
        invitee_uri: inviteeUri
      }, {
        preserveState: true,
        onSuccess: () => {
          setHasScheduled(true)
          setIsSubmitting(false)
        },
        onError: () => {
          setIsSubmitting(false)
        }
      })
    }
  })

  // Se já agendou, mostra mensagem de sucesso
  if (hasScheduled) {
    return (
      <>
        <Head title="Mentoria Agendada - Orientação Profissional" />
        <PageLayout>
          <div className="w-full max-w-2xl">
            <NavigationHeader onBack={() => router.visit("/minha-conta")} />
            <Card className="border-green-200 bg-green-50">
              <CardHeader className="text-center">
                <div className="flex justify-center mb-4">
                  <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center">
                    <CheckCircle className="w-10 h-10 text-green-600" />
                  </div>
                </div>
                <CardTitle className="text-green-800">Mentoria Agendada!</CardTitle>
                <CardDescription className="text-green-700">
                  Você já possui uma sessão de mentoria agendada.
                  {scheduledAt && (
                    <span className="block mt-2">
                      Agendado em: {new Date(scheduledAt).toLocaleDateString("pt-BR")}
                    </span>
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent className="text-center space-y-4">
                <p className="text-sm text-muted-foreground">
                  Você receberá um e-mail de confirmação com os detalhes da sua sessão.
                  Caso precise reagendar, entre em contato conosco.
                </p>

                <p className="text-sm text-muted-foreground">
                  Confira o e-mail cadastrado para acompanhar a confirmação do agendamento.
                </p>

                <Button onClick={() => router.visit("/minha-conta")} variant="outline">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Voltar para Minha Conta
                </Button>
              </CardContent>
            </Card>
          </div>
        </PageLayout>
      </>
    )
  }

  return (
    <>
      <Head title="Agendar Mentoria - Orientação Profissional" />
      <PageLayout>
        <div className="w-full max-w-4xl">
          <NavigationHeader onBack={() => router.visit("/minha-conta")} />

          <Card>
            <CardHeader className="text-center">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 rounded-full bg-yellow-100 flex items-center justify-center">
                  <Calendar className="w-8 h-8 text-yellow-600" />
                </div>
              </div>
              <CardTitle>Agendar Mentoria Individual</CardTitle>
              <CardDescription>
                Escolha o melhor horário para sua sessão de mentoria com Rafaela Ferreira
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isSubmitting ? (
                <div className="flex items-center justify-center py-20">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                    <p className="text-muted-foreground">Salvando agendamento...</p>
                  </div>
                </div>
              ) : (
                <div className="min-h-[650px]">
                  <InlineWidget
                    url={calendlyUrl}
                    prefill={{
                      email: user.email,
                      name: user.name || undefined
                    }}
                    styles={{
                      height: "650px",
                      width: "100%"
                    }}
                    pageSettings={{
                      backgroundColor: "ffffff",
                      primaryColor: "1e40af",
                      textColor: "1e293b",
                      hideEventTypeDetails: false,
                      hideLandingPageDetails: false
                    }}
                  />
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </PageLayout>
    </>
  )
}
